import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiOutlineMail, HiOutlineX, HiOutlineCheck } from 'react-icons/hi';
import { sendMonthlyScoreEmails } from '../../../../emailjs/emailService';
import './MonthlyScoreEmailPopup.scss';

const MonthlyScoreEmailPopup = ({ employees }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendStatus, setSendStatus] = useState({ success: false, count: 0 });
  const dispatch = useDispatch();
  
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
  const currentYear = currentDate.getFullYear();
  
  const scoreData = {
    month: currentMonth,
    year: currentYear,
    score: 14 // This would typically come from real data
  };
  
  const togglePopup = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSelectedEmployees([]);
      setMessage('');
      setSendStatus({ success: false, count: 0 });
    }
  };
  
  const handleEmployeeSelect = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      // Select all employees
      setSelectedEmployees(employees.map(emp => emp.email));
    } else if (!selectedEmployees.includes(value)) {
      setSelectedEmployees([...selectedEmployees, value]);
    }
  };
  
  const removeEmployee = (email) => {
    setSelectedEmployees(selectedEmployees.filter(emp => emp !== email));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedEmployees.length === 0) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Filter the employees array to only include selected employees
      const selectedEmployeeObjects = employees.filter(emp => 
        selectedEmployees.includes(emp.email)
      );
      
      // Send beautiful email notifications using our email service
      const emailResult = await sendMonthlyScoreEmails(
        selectedEmployeeObjects, 
        scoreData, 
        message
      );
      
      // Set status for UI feedback
      setSendStatus({
        success: emailResult.success,
        count: emailResult.sentCount || 0
      });
      
      // Close the popup after successful submission with a slight delay
      if (emailResult.success) {
        setTimeout(() => {
          togglePopup();
          setIsSubmitting(false);
        }, 1500);
      } else {
        setIsSubmitting(false);
      }
      
    } catch (error) {
      console.error('Error sending monthly score emails:', error);
      setIsSubmitting(false);
    }
  };
  
  const getEmployeeName = (email) => {
    const employee = employees.find(emp => emp.email === email);
    return employee ? employee.name : email;
  };
  
  return (
    <>
      <button 
        className="action-button"
        onClick={togglePopup}
        disabled={isSubmitting}
      >
        <HiOutlineMail className="icon" />
        {isSubmitting ? 'Sending...' : 'Send Monthly Score Email'}
      </button>
      
      {isOpen && (
        <div className="email-popup-overlay">
          <div className="email-popup">
            <div className="popup-header">
              <h3>Send Monthly Score Email</h3>
              <button className="close-button" onClick={togglePopup}>
                <HiOutlineX />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="email-form">
              <div className="form-group">
                <label>Select Recipients</label>
                <select 
                  onChange={handleEmployeeSelect} 
                  className="employee-select"
                >
                  <option value="">-- Select Employees --</option>
                  <option value="all">All Employees</option>
                  {employees.map(emp => (
                    <option 
                      key={emp.email} 
                      value={emp.email}
                      disabled={selectedEmployees.includes(emp.email)}
                    >
                      {emp.name} ({emp.email})
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedEmployees.length > 0 && (
                <div className="selected-employees">
                  <label>Selected Recipients</label>
                  <div className="employee-tags">
                    {selectedEmployees.map(email => (
                      <div key={email} className="employee-tag">
                        <span>{getEmployeeName(email)}</span>
                        <button 
                          type="button" 
                          onClick={() => removeEmployee(email)}
                          className="remove-tag"
                        >
                          <HiOutlineX />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="message">Custom Message (Optional)</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a custom message to include in the email..."
                  rows={4}
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={togglePopup}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={selectedEmployees.length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      <span>Sending...</span>
                    </>
                  ) : sendStatus.success ? (
                    <>
                      <HiOutlineCheck className="icon" />
                      <span>Sent Successfully ({sendStatus.count})</span>
                    </>
                  ) : (
                    <>
                      <HiOutlineMail className="icon" />
                      <span>Send Emails</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default MonthlyScoreEmailPopup; 